import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ChevronIcon from '../../../../components/elements/icons/ChevronIcon'
import Traits from '../../../../components/partials/academic-settings/Traits'
import { ERROR } from '../../../../store/types'
import { authHeader, baseUrl } from '../../../../utils'
import axios from 'axios'
import Loader from '../../../../components/elements/Loader'
import PrerequisiteSubjects from '../../../../components/partials/academic-settings/PrerequisiteSubjects'
import ElectiveSubjects from '../../../../components/partials/academic-settings/ElectiveSubjects'
import ScoreGrades from '../../../../components/partials/academic-settings/ScoreGrades'

const AcademicSettings = () => {

    const [settings, setSettings] = useState(null)
    const [loading, setLoading] = useState(true)
    const [reload, setReload] = useState(0)

    const dispatch = useDispatch()

    useEffect(() => {
       const fetchAcademicSettings = async () => {
        try{
            setLoading(true)
            const headers = authHeader()
            const response = await axios.get(`${baseUrl}/academic-settings?expand=prerequisiteSubjects.subject,prerequisiteSubjects.classes,electiveSubjects.subject,electiveSubjects.classes`, { headers })
            setSettings(response.data.data.academicSettings[0])
        } catch(error){
            console.log('error fetching academic settings: ', error)
            dispatch({
                type: ERROR,
                error
            })
        } finally {
            setLoading(false)
        }
       }
       fetchAcademicSettings()
    }, [dispatch, reload])
    const [activeTabs, setActiveTabs] = React.useState({
        traits: false,
        prerequisiteSubjects: false,
        electiveSubjects: false,
        scoreGrades: false
    })

    const toggleTab = (tab) => {
        setActiveTabs({
            ...activeTabs,
            [tab]: !activeTabs[tab]
        })
    }
    
    return (
        <div className="w-full">
            {loading ?
                <Loader />
                :
                <div className='w-full xl:w-8/12 2xl:w-1/2 mx-auto'>
                    <div className={`p-5 rounded-lg bg-gray-50 my-4 border-2 ${activeTabs.traits ? 'border-vcm-purple/20' : 'border-transparent'}`}>
                        <div className='w-full'>
                            <h4 className='font-semibold text-md text-gray-600'>Traits</h4>
                            <p className='text-gray-500 mt-1 text-sm'>
                                Below is a list of academic traits for students. Traits are used to describe the characteristics of students. For example, a student can be described as being hardworking, intelligent, or creative. You can add new traits or edit existing ones as needed.
                            </p>
                            <button onClick={()=>{toggleTab('traits')}} className='mt-2 rounded p-2 text-xs text-gray-700 transition duration-200  hover:bg-vcm-light-purple/20 hover:text-vcm-purple font-semibold flex items-center font-space-grotesk justify-center gap-x-1'>
                                {activeTabs.traits ? 'Hide traits' : 'Show traits'}
                                <ChevronIcon strokeWidth={3} className={`w-3 h-3 transition duration-200 ${activeTabs.traits ? 'rotate-90' : '-rotate-90'}`} />
                            </button>
                        </div>
                        {activeTabs.traits && <div className='w-full mt-4'>
                            <Traits />
                        </div>}
                    </div>

                    <div className={`p-5 rounded-lg bg-gray-50 my-4 border-2 ${activeTabs.prerequisiteSubjects ? 'border-vcm-purple/20' : 'border-transparent'}`}>
                        <div className='w-full'>
                            <h4 className='font-semibold text-md text-gray-600'>Prerequisite subjects</h4>
                            <p className='text-gray-500 mt-1 text-sm'>
                                Below is a list of prerequisite subjects for students. Prerequisite subjects are those that must be passed before respective students can be promoted to the next class. For example, a student must pass Mathematics in order to be promoted to the next class.
                            </p>
                            <button onClick={()=>{toggleTab('prerequisiteSubjects')}} className='mt-2 rounded p-2 text-xs text-gray-700 transition duration-200  hover:bg-vcm-light-purple/20 hover:text-vcm-purple font-semibold flex items-center font-space-grotesk justify-center gap-x-1'>
                                {activeTabs.prerequisiteSubjects ? 'Hide prerequisite subjects' : 'Show prerequisite subjects'}
                                <ChevronIcon strokeWidth={3} className={`w-3 h-3 transition duration-200 ${activeTabs.prerequisiteSubjects ? 'rotate-90' : '-rotate-90'}`} />
                            </button>
                        </div>
                        {activeTabs.prerequisiteSubjects && <div className='w-full mt-4'>
                            <PrerequisiteSubjects subjects={settings?.prerequisiteSubjects || null} reload={()=>{setReload(reload + 1)}} />
                        </div>}
                    </div>

                    <div className={`p-5 rounded-lg bg-gray-50 my-4 border-2 ${activeTabs.electiveSubjects ? 'border-vcm-purple/20' : 'border-transparent'}`}>
                        <div className='w-full'>
                            <h4 className='font-semibold text-md text-gray-600'>Elective subjects</h4>
                            <p className='text-gray-500 mt-1 text-sm'>
                                Below is a list of elective subjects for students. Subjects added here can be chosen by students as electives in their academic programs. You can add, edit, or remove elective subjects as needed to provide students with a variety of options for their studies.
                            </p>
                            <button onClick={()=>{toggleTab('electiveSubjects')}} className='mt-2 rounded p-2 text-xs text-gray-700 transition duration-200  hover:bg-vcm-light-purple/20 hover:text-vcm-purple font-semibold flex items-center font-space-grotesk justify-center gap-x-1'>
                                {activeTabs.electiveSubjects ? 'Hide elective subjects' : 'Show elective subjects'}
                                <ChevronIcon strokeWidth={3} className={`w-3 h-3 transition duration-200 ${activeTabs.electiveSubjects ? 'rotate-90' : '-rotate-90'}`} />
                            </button>
                        </div>
                        {activeTabs.electiveSubjects && <div className='w-full mt-4'>
                            <ElectiveSubjects subjects={settings?.electiveSubjects || null} reload={()=>{setReload(reload + 1)}} />
                        </div>}
                    </div>

                    <div className={`p-5 rounded-lg bg-gray-50 my-4 border-2 ${activeTabs.scoreGrades ? 'border-vcm-purple/20' : 'border-transparent'}`}>
                        <div className='w-full'>
                            <h4 className='font-semibold text-md text-gray-600'>Score Grades</h4>
                            <p className='text-gray-500 mt-1 text-sm'>
                                Below is a list of score grades for students. You can set up score grades to represent student scores in assessments. For example, a score of 90-100 can be represented as an A grade, while a score of 80-89 can be represented as a B grade, and so on.
                            </p>
                            <button onClick={()=>{toggleTab('scoreGrades')}} className='mt-2 rounded p-2 text-xs text-gray-700 transition duration-200  hover:bg-vcm-light-purple/20 hover:text-vcm-purple font-semibold flex items-center font-space-grotesk justify-center gap-x-1'>
                                {activeTabs.scoreGrades ? 'Hide score grades' : 'Show score grades'}
                                <ChevronIcon strokeWidth={3} className={`w-3 h-3 transition duration-200 ${activeTabs.scoreGrades ? 'rotate-90' : '-rotate-90'}`} />
                            </button>
                        </div>
                        {activeTabs.scoreGrades && <div className='w-full mt-4'>
                            <ScoreGrades grades={settings?.scoreGrades || null} reload={()=>{setReload(reload + 1)}} />
                        </div>}
                    </div>
                </div>
            }
        </div>
    )
}

export default AcademicSettings